import { Link } from 'react-router-dom';
import { Linkedin, Instagram, Facebook } from 'lucide-react';
import { NewsletterSubscribe } from '@/components/newsletter/NewsletterSubscribe';
import { useSocialLinks } from '@/hooks/useSiteSettings';
import { trackPlaybook } from '@/lib/playbookAnalytics';

// Custom X (Twitter) icon component
const XIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export function Footer() {
  const { socialLinks } = useSocialLinks();

  return (
    <footer className="border-t bg-muted/30 mt-auto">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
                <span className="text-xl font-bold text-primary-foreground">AI</span>
              </div>
              <span className="font-display font-bold text-xl">ToolHub</span>
            </Link>
            <p className="text-muted-foreground text-sm mb-4">
              AI ToolHub is a curated directory of AI tools. Discover 500+ tools with reviews, pricing, alternatives, and side-by-side comparisons to find the right AI for your workflow.
            </p>
            {/* Social Media Links */}
            <div className="flex items-center gap-3 mb-6">
              {socialLinks.linkedin && (
                <a
                  href={socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {socialLinks.x && (
                <a
                  href={socialLinks.x}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="X (Twitter)"
                >
                  <XIcon className="h-5 w-5" />
                </a>
              )}
              {socialLinks.instagram && (
                <a
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {socialLinks.facebook && (
                <a
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
            </div>
            {/* Newsletter */}
            <div className="pt-4 border-t border-border/50">
              <h4 className="font-display font-semibold mb-2 text-sm">Stay Updated</h4>
              <p className="text-muted-foreground text-xs mb-3">
                Get the latest AI tools delivered to your inbox.
              </p>
              <NewsletterSubscribe variant="inline" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-display font-semibold mb-4">Product</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
                  Explore Tools
                </Link>
              </li>
              <li>
                <Link to="/ai-tools-playbook" onClick={() => trackPlaybook('playbook_promo_click', 'footer')} className="text-muted-foreground hover:text-foreground transition-colors">
                  AI Tools Playbook
                </Link>
              </li>
              <li>
                <Link to="/categories" className="text-muted-foreground hover:text-foreground transition-colors">
                  Categories
                </Link>
              </li>
              <li>
                <Link to="/best-free-ai-tools" className="text-muted-foreground hover:text-foreground transition-colors">
                  Free AI Tools
                </Link>
              </li>
              <li>
                <Link to="/middle-east" className="text-muted-foreground hover:text-foreground transition-colors">
                  Middle East AI
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/marketplace" className="text-muted-foreground hover:text-foreground transition-colors">
                  AI Creators
                </Link>
              </li>
              <li>
                <Link to="/submit" className="text-muted-foreground hover:text-foreground transition-colors">
                  Submit Tool
                </Link>
              </li>
            </ul>
          </div>

          {/* Popular Tools - Internal Linking for SEO */}
          <div>
            <h3 className="font-display font-semibold mb-4">Popular Tools</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/compare/vibe-coding" className="text-muted-foreground hover:text-foreground transition-colors">
                  Compare AI Coders
                </Link>
              </li>
              <li>
                <Link to="/alternatives" className="text-muted-foreground hover:text-foreground transition-colors">
                  Find Alternatives
                </Link>
              </li>
              <li>
                <Link to="/tool-battles" className="text-muted-foreground hover:text-foreground transition-colors">
                  Tool Battles
                </Link>
              </li>
              <li>
                <Link to="/news" className="text-muted-foreground hover:text-foreground transition-colors">
                  AI News
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-display font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/premium?tab=ads" className="text-muted-foreground hover:text-foreground transition-colors">
                  Advertise
                </Link>
              </li>
              <li>
                <Link to="/help" className="text-muted-foreground hover:text-foreground transition-colors">
                  Help Center
                </Link>
              </li>
              <li>
                <Link to="/sitemap" className="text-muted-foreground hover:text-foreground transition-colors">
                  Site Map
                </Link>
              </li>
            </ul>
          </div>

          {/* Policies */}
          <div>
            <h3 className="font-display font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/legal" className="text-muted-foreground hover:text-foreground transition-colors">
                  All Policies
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">
                  Pricing
                </Link>
              </li>
            </ul>
          </div>

        </div>


        <div className="mt-8 pt-6 text-center text-muted-foreground text-sm space-y-3">
          <p className="font-medium text-foreground/80">
            Operated by Next Mind Digital Artificial Intelligence Developing Services
          </p>
          <p>&copy; {new Date().getFullYear()} AI ToolHub. All rights reserved.</p>
          <p>
            Proudly made in Dubai 🇦🇪 · Built with ❤️ by{' '}
            <a 
              href="https://nextminddigital.com/built-by-nextmind" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              NextMind Digital Labs
            </a>
          </p>
          <p>
            A product of{' '}
            <a 
              href="https://nextminddigital.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              NextMindDigital.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
